import { QueryKey, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { authAxiosClient } from '@/lib/axios';
import { withLogger } from '@/utils';
import { UserProfile } from '../types';
import { usersQueryKeys } from '../data';
import { useMemo } from 'react';

type Response = {
  data: UserProfile;
};

type CustomQueryOptions<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = Omit<
  UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  'queryKey' | 'queryFn'
> &
  Partial<Pick<UseQueryOptions, 'queryKey'>> & {
    // NOTE: Experimental
    shouldPrependOrigKey?: boolean;
  };

export const fetchCurrentUser = withLogger(
  async (): Promise<Response['data']> => {
    const {
      data: { data: rawUser },
    } = await authAxiosClient.get<{
      success: boolean;
      data: UserProfile;
    }>('/auth/profile');

    return rawUser;
  },
);

export const useUserQuery = (
  queryOptions?: CustomQueryOptions<Response['data']>,
) => {
  const queryKey = useMemo(
    () =>
      queryOptions?.queryKey
        ? queryOptions?.shouldPrependOrigKey
          ? [...usersQueryKeys.getUserInfo, ...queryOptions.queryKey]
          : queryOptions.queryKey
        : usersQueryKeys.getUserInfo,
    [queryOptions],
  );
  return useQuery({
    queryKey: usersQueryKeys.getUserProfile,
    ...queryOptions,
    queryFn: fetchCurrentUser,
    staleTime: Infinity,
  });
};
